import type { ColumnDef } from '@tanstack/react-table';
import { SETTINGS_PERMISSIONS } from '@/constants/permissions';
import { ExternalLink } from 'lucide-react';
import {
  createActionsColumn,
  createSerialColumn,
  WrappingCellText,
} from '@/components/common/DataTable';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowEditDeleteButtons } from '@/components/common/RowActions';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate } from '@/utils/date';
import type { WebUrl } from '../types/webUrl.types';
import { getWebUrlActionName, toSingleSpacedText } from '../utils/webUrlText';

interface WebUrlColumnActions {
  onEdit: (webUrl: WebUrl) => void;
  onDelete: (webUrl: WebUrl) => void;
}

export function getWebUrlColumns(
  searchText?: string,
  actions?: WebUrlColumnActions,
): ColumnDef<WebUrl, unknown>[] {
  return [
    createSerialColumn<WebUrl>(),
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.title ?? EMPTY_VALUE} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => {
        const description = row.original.description;
        if (!description) return <EmptyValue />;
        return (
          <WrappingCellText width="wide">
            <HighlightText text={toSingleSpacedText(description)} highlightedText={searchText} />
          </WrappingCellText>
        );
      },
    },
    {
      accessorKey: 'actionLink',
      header: 'Link',
      cell: ({ row }) => {
        const link = row.original.actionLink;
        if (!link) return <EmptyValue />;
        return (
          <Tooltip>
            <TooltipTrigger
              render={
                <a
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex max-w-xs items-center gap-1.5 text-primary hover:underline"
                />
              }
            >
              <span className="truncate">
                <HighlightText text={link} highlightedText={searchText} />
              </span>
              <ExternalLink className="size-3.5 shrink-0" aria-hidden />
            </TooltipTrigger>
            <TooltipContent className="break-all">{link}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      id: 'activeWindow',
      header: 'Active Window',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm">
          {formatDisplayDate(row.original.activeFrom)} - {formatDisplayDate(row.original.activeTo)}
        </span>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    ...(actions
      ? [
          createActionsColumn<WebUrl>((webUrl) => (
            <RowEditDeleteButtons
              item={webUrl}
              itemName={getWebUrlActionName(webUrl)}
              onEdit={actions.onEdit}
              onDelete={actions.onDelete}
              editRequires={SETTINGS_PERMISSIONS.WEB_URLS_EDIT}
              deleteRequires={SETTINGS_PERMISSIONS.WEB_URLS_DELETE}
            />
          )),
        ]
      : []),
  ];
}
